import { Home, ArrowLeft, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DiagnosticLogs } from '@/components/common/DiagnosticLogs';

export const NotFoundPage = () => {
  const navigate = useNavigate();

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[hsl(var(--background))]"
      data-testid="notFound_root"
    >
      {/* Floating stars scattered across the full background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-[10%] left-[15%] animate-pulse"
          style={{ animationDelay: '0s' }}
        >
          <svg width="14" height="14" viewBox="0 0 12 12">
            <polygon
              points="6,0 7.5,4.5 12,4.5 8.25,7.5 9.75,12 6,9 2.25,12 3.75,7.5 0,4.5 4.5,4.5"
              fill="hsl(var(--border))"
            />
          </svg>
        </div>
        <div
          className="absolute top-[20%] right-[20%] animate-pulse"
          style={{ animationDelay: '0.7s' }}
        >
          <svg width="10" height="10" viewBox="0 0 12 12">
            <polygon
              points="6,0 7.5,4.5 12,4.5 8.25,7.5 9.75,12 6,9 2.25,12 3.75,7.5 0,4.5 4.5,4.5"
              fill="hsl(var(--muted-foreground))"
              opacity="0.35"
            />
          </svg>
        </div>
        <div
          className="absolute top-[35%] left-[8%] animate-pulse"
          style={{ animationDelay: '1.4s' }}
        >
          <svg width="8" height="8" viewBox="0 0 12 12">
            <polygon
              points="6,0 7.5,4.5 12,4.5 8.25,7.5 9.75,12 6,9 2.25,12 3.75,7.5 0,4.5 4.5,4.5"
              fill="hsl(var(--muted-foreground))"
              opacity="0.25"
            />
          </svg>
        </div>
        <div
          className="absolute top-[15%] right-[35%] animate-pulse"
          style={{ animationDelay: '2.1s' }}
        >
          <svg width="6" height="6" viewBox="0 0 12 12">
            <polygon
              points="6,0 7.5,4.5 12,4.5 8.25,7.5 9.75,12 6,9 2.25,12 3.75,7.5 0,4.5 4.5,4.5"
              fill="hsl(var(--muted-foreground))"
              opacity="0.2"
            />
          </svg>
        </div>
        <div
          className="absolute bottom-[25%] left-[25%] animate-pulse"
          style={{ animationDelay: '0.3s' }}
        >
          <svg width="9" height="9" viewBox="0 0 12 12">
            <polygon
              points="6,0 7.5,4.5 12,4.5 8.25,7.5 9.75,12 6,9 2.25,12 3.75,7.5 0,4.5 4.5,4.5"
              fill="hsl(var(--border))"
              opacity="0.45"
            />
          </svg>
        </div>
        <div
          className="absolute bottom-[30%] right-[12%] animate-pulse"
          style={{ animationDelay: '1.8s' }}
        >
          <svg width="11" height="11" viewBox="0 0 12 12">
            <polygon
              points="6,0 7.5,4.5 12,4.5 8.25,7.5 9.75,12 6,9 2.25,12 3.75,7.5 0,4.5 4.5,4.5"
              fill="hsl(var(--border))"
              opacity="0.3"
            />
          </svg>
        </div>
        <div
          className="absolute top-[55%] right-[30%] animate-pulse"
          style={{ animationDelay: '0.9s' }}
        >
          <svg width="7" height="7" viewBox="0 0 12 12">
            <polygon
              points="6,0 7.5,4.5 12,4.5 8.25,7.5 9.75,12 6,9 2.25,12 3.75,7.5 0,4.5 4.5,4.5"
              fill="hsl(var(--muted-foreground))"
              opacity="0.2"
            />
          </svg>
        </div>
        <div
          className="absolute bottom-[15%] right-[40%] animate-pulse"
          style={{ animationDelay: '1.1s' }}
        >
          <svg width="5" height="5" viewBox="0 0 12 12">
            <polygon
              points="6,0 7.5,4.5 12,4.5 8.25,7.5 9.75,12 6,9 2.25,12 3.75,7.5 0,4.5 4.5,4.5"
              fill="hsl(var(--muted-foreground))"
              opacity="0.3"
            />
          </svg>
        </div>
      </div>

      {/* Astronaut illustration */}
      <div className="relative mb-8 w-64 h-52 select-none">
        <div
          className="absolute inset-0 flex items-center justify-center animate-bounce"
          style={{ animationDuration: '3s' }}
        >
          <svg
            width="140"
            height="140"
            viewBox="0 0 120 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="60"
              cy="45"
              r="28"
              fill="hsl(var(--secondary))"
              stroke="hsl(var(--border))"
              strokeWidth="2.5"
            />
            <ellipse cx="60" cy="43" rx="18" ry="16" fill="hsl(var(--foreground))" opacity="0.08" />
            <circle cx="52" cy="41" r="2.5" fill="hsl(var(--muted-foreground))" />
            <circle cx="68" cy="41" r="2.5" fill="hsl(var(--muted-foreground))" />
            <path
              d="M54 51 Q60 48 66 51"
              stroke="hsl(var(--muted-foreground))"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
            <rect
              x="44"
              y="72"
              width="32"
              height="24"
              rx="8"
              fill="hsl(var(--secondary))"
              stroke="hsl(var(--border))"
              strokeWidth="2"
            />
            <rect x="38" y="75" width="8" height="16" rx="3" fill="hsl(var(--border))" />
            <path
              d="M44 80 Q32 72 28 82"
              stroke="hsl(var(--border))"
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M76 80 Q88 72 92 82"
              stroke="hsl(var(--border))"
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M52 96 L48 110"
              stroke="hsl(var(--border))"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d="M68 96 L72 110"
              stroke="hsl(var(--border))"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <circle
              cx="46"
              cy="112"
              r="4"
              fill="hsl(var(--secondary))"
              stroke="hsl(var(--border))"
              strokeWidth="1.5"
            />
            <circle
              cx="74"
              cy="112"
              r="4"
              fill="hsl(var(--secondary))"
              stroke="hsl(var(--border))"
              strokeWidth="1.5"
            />
            <line x1="60" y1="17" x2="60" y2="8" stroke="hsl(var(--border))" strokeWidth="2" />
            <circle cx="60" cy="6" r="3" fill="hsl(var(--error))" opacity="0.6" />
          </svg>
        </div>
      </div>

      {/* Text */}
      <h1
        className="text-8xl font-bold text-[hsl(var(--foreground))] mb-2 tracking-tighter"
        data-testid="notFound_code"
      >
        404
      </h1>
      <h2
        className="text-xl font-semibold text-[hsl(var(--foreground))] mb-2"
        data-testid="notFound_title"
      >
        We couldn't find that page
      </h2>
      <p
        className="text-sm text-[hsl(var(--muted-foreground))] max-w-sm mx-auto text-center mb-4"
        data-testid="notFound_description"
      >
        The page you're looking for may have moved or no longer exists. Let's get you back on track.
      </p>
      <div
        className="mx-4 mb-8 flex max-w-sm items-start gap-2 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] px-4 py-3 text-left text-xs text-[hsl(var(--foreground))]"
        data-testid="notFound_multiTabHint"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--muted-foreground))]" aria-hidden />
        <p>
          <span className="font-semibold">
            Opened a link from an email, or signed in on another tab?
          </span>{' '}
          Being signed in on another tab can stop links like password resets from opening. Sign out
          or close your other tabs, then open the link again.
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3" data-testid="notFound_actions">
        <button
          onClick={() => navigate('/')}
          className="flex items-center justify-center gap-2 h-11 px-8 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-full transition-colors"
          data-testid="notFound_goHomeBtn"
        >
          <Home className="h-4 w-4" />
          Go Home
        </button>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center gap-2 h-11 px-8 text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-full transition-colors"
          data-testid="notFound_goBackBtn"
        >
          <ArrowLeft className="h-4 w-4" />
          Go Back
        </button>
      </div>

      {/* Diagnostic logs — bottom right */}
      <div className="fixed bottom-6 right-6" data-testid="notFound_diagnostics">
        <DiagnosticLogs errorContext="404 - Page Not Found" />
      </div>
    </div>
  );
};

export default NotFoundPage;
